import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import type { MakeCallOptions } from '../../channels/voice/voiceCallFeature';
import type { AvatarLiveSessionKind, AvatarSessionContext, AvatarSessionState } from '../../session';
import type { CamlinAvatarConfig } from '../../sdk/CamlinAvatar';
import { useVoiceFeatures } from '../utils/voiceUtils';
import type { ModelConfig } from '../modelRegistry';

import { useAvatarHealth, type AvatarHealthState } from './useAvatarHealth';
import { useAvatarAnalytics } from './useAvatarAnalytics';
import { useAvatarLiveEventBridge } from './useAvatarLiveEventBridge';
import { useAvatarShellState } from './useAvatarShellState';
import { useHostedLiveControllerActions } from './useHostedLiveControllerActions';
import { useHostedLiveControllerEffects } from './useHostedLiveControllerEffects';
import { useHostedLiveState, type HostedLivePromptField, type HostedLivePromptOption, type HostedLiveRefs } from './useHostedLiveState';
import { useLiveAvatarConfig } from './useLiveAvatarConfig';
import { useLivePromptPlayback } from './useLivePromptPlayback';
import { useLiveProvisionIdentity } from './useLiveProvisionIdentity';
import { useLiveSessionContext } from './useLiveSessionContext';
import { useLiveSessionRequest, type RequestLiveSessionFn } from './useLiveSessionRequest';
import { useLiveVisualChannel } from './useLiveVisualChannel';
import { useTurnCredentials } from './useTurnCredentials';
import { useVoiceBrowserAccess, type BrowserAccessPromptState } from './useVoiceBrowserAccess';

type AnimateCallSpeakValue = boolean | { blendData?: any };
type ToastType = 'info' | 'success' | 'warning' | 'error';

interface AvatarShellRef {
  playBlendshapes?: (frames: Array<{ time: number; values: Record<string, number> }>) => void;
}

export interface UseHostedLiveControllerOptions {
  activeListen: boolean;
  /**
   * Host owns the conversation brain (Decision Memory / AgentLink). Threaded
   * into prompt-playback so host-managed WebRTC still arms browser STT after
   * the greeting — CamlinVoice ASR is not enough when the host consumes
   * `avatar:transcript` instead of runtime `/generate`.
   */
  handleInputExternally?: boolean;
  /** Suppress spoken prompt playback until unlockAudio — see useLivePromptPlayback. */
  holdSpeechUntilUnlock?: boolean;
  /**
   * Instance id of the avatar this controller drives (same id the caller
   * passes to CamlinAvatar/Scene). Threaded into the `linkedAvatarConfig`
   * replay so it targets only that avatar. Optional — the standalone
   * runtime-host shell (`App.tsx`) has a single id-less avatar.
   */
  avatarInstanceId?: string;
  avatarRef: MutableRefObject<AvatarShellRef | null>;
  builderDiagramId?: string;
  cameraZoom: number;
  canvasContainerRef: MutableRefObject<HTMLDivElement | null>;
  handoffTokenCamlin: string;
  handleVoiceEvent: (eventName: string, data: any, props?: any) => void;
  initialLiveConnectRequested?: boolean;
  initialSessionContext?: Partial<AvatarSessionContext> | AvatarSessionContext | null;
  isCallActiveRef: MutableRefObject<boolean>;
  isEmbeddedBuilderView: boolean;
  isMobileCall: boolean;
  isVisualOnly: boolean;
  isWebrtcCall: boolean;
  liveDestination?: string | null;
  liveModeRequested: boolean;
  noSip: boolean;
  projectTokenCamlin: string;
  queryParams: URLSearchParams;
  requestedLiveSessionKind?: AvatarLiveSessionKind;
  requestedLiveVoiceId?: string;
  resolveLinkedAvatarModelId: (
    explicitAvatarModelId?: string | null,
    avatarConfig?: Record<string, unknown> | null
  ) => string | null;
  resolvedTtsBaseUrl: string;
  runtimeUrl?: string | null;
  selectedModel: ModelConfig;
  setAnimateCallSpeak: Dispatch<SetStateAction<AnimateCallSpeakValue>>;
  setAudioSource: (source: string | null) => void;
  setDisplayText: (value: string) => void;
  setIsCallActive: (value: boolean) => void;
  setIsMobileCall: (value: boolean) => void;
  setIsWebrtcCall: (value: boolean) => void;
  setSpeak: (value: boolean) => void;
  showToast: (message: string, type?: ToastType) => void;
  sipWebSocketUrl?: string;
  stopAudioPlayback: () => void;
  unlockAudio: () => Promise<void>;
  verboseAvatarDebug: boolean;
  voiceUrl: string;
}

export interface UseHostedLiveControllerReturn {
  activeAvatarModel: ModelConfig;
  /**
   * Aggregate avatar health (TURN/WebRTC/audio/WebGL/live-session). Pass
   * to `<AvatarHealthBadge state={...} />` to render the always-on
   * status pip. Also dispatched on `window` as `avatar:health` for
   * external observability (Sentry, Datadog, etc).
   */
  avatarHealth: AvatarHealthState;
  avatarLoadingMessage: string | null;
  browserAccessPrompt: BrowserAccessPromptState | null;
  canvasContainerSize: { width: number; height: number } | null;
  clearLiveAutoListenTimer: () => void;
  clearLivePlaybackCountdown: () => void;
  clearLiveSpeakingTimeout: () => void;
  clearLiveVoiceRetryTimer: () => void;
  closeBrowserAccessPrompt: () => void;
  currentSession: any;
  dispatchAvatarEvent: (eventName: string, detail: unknown) => void;
  effectiveCameraZoom: number;
  motionParallaxEnabled: boolean;
  effectiveSkipTeethAnimation: boolean | undefined;
  ensureVoiceBrowserAccess: (requireMicrophone: boolean) => Promise<boolean>;
  /**
   * Quality-resolved (and mobile-overridden) model URL. App.tsx must pass
   * this — NOT `activeAvatarModel.url` — into `<AvatarSceneShell>` so that
   * the renderer's <Canvas>+<Scene> branch fetches the SD GLB on phones.
   * See useLiveAvatarConfig.ts for the resolution logic.
   */
  resolvedModelUrl: string;
  /**
   * Merged appearance (skin/eye/hair/background colours) — project blueprint
   * settings layered over the model registry's `defaultAppearance`. App.tsx
   * must dispatch this as an `avatar:config:update` window event once the
   * avatar is ready so the renderer applies the colours. Avatar.tsx is the
   * listener (`window.addEventListener('avatar:config:update', ...)`). Null
   * when nothing to apply (use GLB-baked materials).
   */
  liveAvatarAppearance: Record<string, unknown> | null;
  liveAvatarConfig: Record<string, unknown> | null;
  /**
   * True once the runtime-service has sent us `visual.linked` with the
   * project's avatar config (model id and/or appearance overrides). Used
   * by the runtime host App to gate the avatar mount on Visual IVR so we
   * don't load a default model first and then swap to the project model
   * (the swap is what OOMs iOS Safari WebKit). Always true for the /live
   * route which doesn't go through the visual handoff handshake.
   */
  hasReceivedVisualLink: boolean;
  isAvatarReady: boolean;
  isInVoiceCall: boolean;
  isVoiceInitialized: boolean;
  initiateCall: (target: string, options?: MakeCallOptions) => boolean;
  liveAssistantText: string;
  liveChatEventId: number;
  liveChatText: string;
  liveConnectRequested: boolean;
  liveConnectionError: string | null;
  liveModeActive: boolean;
  livePromptFields: HostedLivePromptField[];
  livePromptOptions: HostedLivePromptOption[];
  liveSessionContext: AvatarSessionContext | null;
  liveSessionState: AvatarSessionState | null;
  liveTranscriptEventId: number;
  liveUserTranscript: string;
  liveUserTranscriptFinal: boolean;
  liveVoiceCallEnabled: boolean;
  markAvatarReady: () => void;
  refs: HostedLiveRefs;
  requestLiveSession: RequestLiveSessionFn;
  retryBrowserAccess: () => Promise<void>;
  setIsInVoiceCall: (value: boolean) => void;
  setLiveAssistantText: Dispatch<SetStateAction<string>>;
  setLiveConnectRequested: Dispatch<SetStateAction<boolean>>;
  setLiveConnectionError: Dispatch<SetStateAction<string | null>>;
  setLiveStatus: (
    status: import('../../session').AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
  setLiveTranscriptEventId: Dispatch<SetStateAction<number>>;
  setLiveUserTranscript: Dispatch<SetStateAction<string>>;
  setLiveUserTranscriptFinal: Dispatch<SetStateAction<boolean>>;
  setPromptPlaybackUnlocked: (unlocked: boolean) => void;
  sharedLiveAvatarConfig: CamlinAvatarConfig;
  stopLiveSyntheticAudioSync: (finalCurrentTimeSeconds?: number) => void;
  handleLivePromptOptionSelect: (value: string, label: string) => void;
}

export function useHostedLiveController({
  activeListen,
  handleInputExternally,
  holdSpeechUntilUnlock,
  avatarInstanceId,
  avatarRef,
  builderDiagramId,
  cameraZoom,
  canvasContainerRef,
  handoffTokenCamlin,
  handleVoiceEvent,
  initialLiveConnectRequested,
  initialSessionContext,
  isCallActiveRef,
  isEmbeddedBuilderView,
  isMobileCall,
  isVisualOnly,
  isWebrtcCall,
  liveDestination,
  liveModeRequested,
  noSip,
  projectTokenCamlin,
  queryParams,
  requestedLiveSessionKind,
  requestedLiveVoiceId,
  resolveLinkedAvatarModelId,
  resolvedTtsBaseUrl,
  runtimeUrl,
  selectedModel,
  setAnimateCallSpeak,
  setAudioSource,
  setDisplayText,
  setIsCallActive,
  setIsMobileCall,
  setIsWebrtcCall,
  setSpeak,
  showToast,
  sipWebSocketUrl,
  stopAudioPlayback,
  unlockAudio,
  verboseAvatarDebug,
  voiceUrl
}: UseHostedLiveControllerOptions): UseHostedLiveControllerReturn {
  const {
    linkedAvatarConfig,
    linkedAvatarModelId,
    liveAssistantText,
    liveChatEventId,
    liveChatText,
    liveConnectRequested,
    liveConnectionError,
    livePromptFields,
    livePromptOptions,
    liveSessionContext,
    liveSessionState,
    liveTranscriptEventId,
    liveUserTranscript,
    liveUserTranscriptFinal,
    refs,
    setLinkedAvatarConfig,
    setLinkedAvatarModelId,
    setLiveAssistantText,
    setLiveChatEventId,
    setLiveChatText,
    setLiveConnectRequested,
    setLiveConnectionError,
    setLivePromptFields,
    setLivePromptOptions,
    setLiveSessionContext,
    setLiveSessionState,
    setLiveTranscriptEventId,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal
  } = useHostedLiveState({
    builderDiagramId,
    handoffTokenCamlin,
    initialLiveConnectRequested,
    initialSessionContext,
    isVisualOnly,
    liveModeRequested,
    noSip,
    projectTokenCamlin,
    queryParams,
    requestedLiveSessionKind
  });

  const {
    liveAudioSyncAnimationRef,
    liveAudioSyncDurationRef,
    liveAudioSyncStartTimeRef,
    liveAutoListenArmedRef,
    liveAutoListenPendingRef,
    liveAutoListenTimerRef,
    liveAvatarModelIdRef,
    liveClientRef,
    liveManualHangupRef,
    livePlaybackCountdownRef,
    livePromptAudioDedupRef,
    livePromptAudioRequestRef,
    livePromptListeningRef,
    livePromptPlaybackUnlockedRef,
    livePromptTextRef,
    liveRetryCountRef,
    liveRetryTimerRef,
    liveSessionContextRef,
    liveSessionStreamRef,
    liveSpeakingRef,
    liveSpeakingTimeoutRef,
    liveThinkingRef,
    liveVoiceAttemptRef,
    liveVoicePromptStartedRef,
    liveVoiceRetryCountRef,
    liveVoiceRetryTimerRef,
    requestLiveSessionRef
  } = refs;

  const liveModeActive = liveModeRequested || liveSessionContext?.channel === 'live' || liveSessionContext?.metadata?.liveMode === true;

  // Mint short-lived ICE/TURN credentials from the runtime-service in the
  // background so they're warm by the time the user clicks Listen. The
  // hook writes into the module-level cache that voiceCallFeature.makeCall
  // reads from — no consumer-side change needed. Disabled in visual-only
  // mode (no WebRTC) and when no runtime URL is configured (test paths).
  // See useTurnCredentials.ts header for the full lifecycle contract.
  //
  // The returned `turnCredentialsState` feeds `useAvatarHealth` below so
  // the on-screen status pip can show 'STUN-only fallback' (warn) when
  // the dynamic-fetch path is degraded.
  const turnCredentialsState = useTurnCredentials({
    runtimeUrl,
    enabled: !isVisualOnly,
  });
  // Three live-mode shapes (`useLiveVisualChannel.ts` line 472-474 documents
  // the audio-playback contract that depends on these flags):
  //
  //   - Visual IVR        (isVisualOnly=true,  liveVoiceCallEnabled=false): phone plays audio
  //   - Browser SIP/live  (isVisualOnly=false, liveVoiceCallEnabled=true ): SIP plays audio
  //   - Browser-only chat (isVisualOnly=false, liveVoiceCallEnabled=false): browser TTS plays
  //
  // The third case is AgentLink and Decision Memory full-screen — they are
  // browser-side audio with no SIP destination. Before the Apr-30 2026
  // live-mode reliability fix, both were incorrectly flagged as "Browser
  // SIP/live" because `liveVoiceCallEnabled` only keyed off `isVisualOnly`,
  // which made the SDK try to dial a SIP destination that doesn't exist
  // and silently fail. Distinguish the third case by whether the host
  // configured a `liveDestination` — no destination ⇒ no SIP path possible
  // ⇒ browser-only chat. `noSip` from non-live callers is still honored
  // later when `useVoiceFeatures` initialises.
  const hasVoiceDestination = Boolean(liveDestination && String(liveDestination).trim());
  const liveTransport = isVisualOnly || !hasVoiceDestination ? 'websocket' : 'webrtc';
  const liveVoiceCallEnabled = liveModeActive && !isVisualOnly && hasVoiceDestination;
  const liveSessionToken = liveSessionContext?.handoffToken;

  const { liveProvisionUserId } = useLiveProvisionIdentity({
    isVisualOnly,
    liveModeActive,
    liveSessionContext,
    liveVoiceCallEnabled,
    noSip,
    queryParams
  });

  // Defer SIP/WebRTC initialization until user clicks Listen (liveConnectRequested)
  // so the UI doesn't show "Connecting Voice" immediately on entering live mode
  const deferSipInit = liveModeActive && !isVisualOnly && !liveConnectRequested;

  // @ts-ignore - legacy voice utility remains loosely typed
  const {
    isVoiceInitialized,
    currentSession,
    isInVoiceCall,
    setIsInVoiceCall,
    initiateCall
  } = useVoiceFeatures(
    projectTokenCamlin,
    handoffTokenCamlin,
    handleVoiceEvent,
    {
      noSip: deferSipInit || (liveModeActive ? isVisualOnly : (noSip || isVisualOnly)),
      provisionUserId: liveProvisionUserId,
      voiceApiBaseUrl: voiceUrl,
      sipWebSocketUrl,
      disableLegacySocket: liveModeActive,
      onRegistrationExhausted: () => {
        setLiveConnectionError('Live voice could not register. Stop and tap Listen to try again.');
      }
    }
  );

  const {
    browserAccessPrompt,
    closeBrowserAccessPrompt,
    ensureVoiceBrowserAccess,
    retryBrowserAccess
  } = useVoiceBrowserAccess({
    isInVoiceCall,
    isMobileCall,
    isWebrtcCall,
    liveVoiceCallEnabled,
    unlockAudio
  });

  const {
    clearLiveAutoListenTimer,
    clearLiveVoiceRetryTimer,
    dispatchAvatarEvent,
    setLiveStatus
  } = useHostedLiveControllerEffects({
    refs: {
      liveAutoListenTimerRef,
      liveSessionContextRef,
      liveVoiceRetryTimerRef
    },
    avatarInstanceId,
    linkedAvatarConfig,
    liveConnectionError,
    liveModeActive,
    liveSessionState,
    liveTransport,
    resolvedTtsBaseUrl,
    setDisplayText,
    setLiveSessionState
  });

  const {
    activeAvatarModel,
    resolvedModelUrl,
    liveAvatarAppearance,
    liveAvatarConfig,
    effectiveCameraZoom,
    motionParallaxEnabled,
    effectiveSkipTeethAnimation,
    liveTtsVoiceId,
    sharedLiveAvatarConfig
  } = useLiveAvatarConfig({
    selectedModel,
    linkedAvatarModelId,
    linkedAvatarConfig,
    projectToken: projectTokenCamlin,
    runtimeUrl,
    requestedLiveVoiceId,
    liveModeActive,
    isEmbeddedBuilderView,
    cameraZoom,
    isMobileCall,
    isWebrtcCall,
    isInVoiceCall,
    liveAvatarModelIdRef,
    resolveLinkedAvatarModelId
  });

  const {
    handleLiveAvatarModelReady,
    handleLivePromptOptionSelect
  } = useHostedLiveControllerActions({
    liveClientRef,
    liveModeActive
  });

  const {
    avatarLoadingMessage,
    canvasContainerSize,
    isAvatarReady,
    markAvatarReady
  } = useAvatarShellState({
    activeAvatarModelId: activeAvatarModel.id,
    activeAvatarModelName: activeAvatarModel.name,
    // Use the quality-resolved URL here too. Although this hook only drives
    // loading state, keying it on the HD URL made the Visual IVR shell think
    // the avatar model changed separately from the actual SD-rendered model,
    // which can lead to duplicate HD/SD fetches and extra loading cycles.
    activeAvatarModelUrl: resolvedModelUrl,
    avatarRef,
    canvasContainerRef,
    onAvatarModelReady: handleLiveAvatarModelReady,
    verboseAvatarDebug
  });

  const resolveCurrentPromptVoiceId = useCallback(() => liveTtsVoiceId, [liveTtsVoiceId]);
  const hasDirectAvatarPlayback = useCallback(
    () => typeof avatarRef.current?.playBlendshapes === 'function',
    [avatarRef]
  );

  const {
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    stopLiveSyntheticAudioSync,
    finishLiveSpeaking,
    shouldSuppressInitialActiveListenPrompt,
    playLivePromptAudio,
    dispatchLiveBlendshapePlayback,
    setPromptPlaybackUnlocked
  } = useLivePromptPlayback({
    refs: {
      liveAudioSyncAnimationRef,
      liveAudioSyncStartTimeRef,
      liveAudioSyncDurationRef,
      livePromptAudioRequestRef,
      livePromptAudioDedupRef,
      livePromptListeningRef,
      livePromptPlaybackUnlockedRef,
      liveVoicePromptStartedRef,
      liveSpeakingRef,
      liveThinkingRef,
      livePlaybackCountdownRef,
      liveSpeakingTimeoutRef,
      liveAutoListenArmedRef,
      liveAutoListenPendingRef,
      isCallActiveRef
    },
    activeListen,
    handleInputExternally,
    holdSpeechUntilUnlock,
    liveVoiceCallEnabled,
    isVisualOnly,
    requestedLiveVoiceId: liveTtsVoiceId,
    resolvedTtsBaseUrl,
    resolveCurrentPromptVoiceId,
    stopAudioPlayback,
    setAudioSource,
    setSpeak,
    dispatchAvatarEvent,
    setLiveStatus,
    hasDirectAvatarPlayback
  });

  const {
    ensureLiveContext,
    buildContextualChatLiveRuntimeContext
  } = useLiveSessionContext({
    builderDiagramId,
    liveConnectionError,
    liveProvisionUserId,
    liveSessionContext,
    liveSessionContextRef,
    liveSessionStatus: liveSessionState?.status,
    projectTokenCamlin,
    setLiveSessionContext,
    setLiveStatus
  });

  const { requestLiveSession } = useLiveSessionRequest({
    refs: {
      requestLiveSessionRef,
      liveManualHangupRef,
      liveVoicePromptStartedRef,
      liveVoiceRetryTimerRef,
      liveVoiceRetryCountRef,
      liveVoiceAttemptRef,
      livePromptListeningRef,
      liveSpeakingRef,
      liveThinkingRef,
      liveAvatarModelIdRef
    },
    ensureLiveContext,
    clearLiveVoiceRetryTimer,
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    stopLiveSyntheticAudioSync,
    dispatchAvatarEvent,
    initiateCall,
    avatarLoadingMessage,
    liveModeActive,
    isAvatarReady,
    isVoiceInitialized,
    liveDestination,
    liveVoiceId: liveTtsVoiceId,
    isVisualOnly,
    closeBrowserAccessPrompt,
    setIsCallActive,
    setIsWebrtcCall,
    setIsInVoiceCall,
    setLiveConnectRequested,
    setLiveConnectionError,
    setDisplayText,
    setLiveAssistantText,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal,
    setLiveStatus,
    showToast
  });

  useAvatarLiveEventBridge({
    avatarRef,
    projectTokenCamlin,
    verboseAvatarDebug,
    liveModeActive,
    liveSessionToken,
    isVoiceInitialized,
    isVisualOnly,
    noSip,
    ensureLiveContext,
    requestLiveSession,
    initiateCall,
    hasLiveClient: () => Boolean(liveClientRef.current),
    setAnimateCallSpeak
  });

  useLiveVisualChannel({
    refs: {
      liveClientRef,
      liveSessionStreamRef,
      liveSessionContextRef,
      liveRetryTimerRef,
      liveRetryCountRef,
      liveVoiceRetryTimerRef,
      liveVoiceRetryCountRef,
      liveAvatarModelIdRef,
      liveManualHangupRef,
      livePromptTextRef,
      livePlaybackCountdownRef,
      livePromptListeningRef,
      liveSpeakingRef,
      liveThinkingRef,
      livePromptAudioRequestRef,
      livePromptAudioDedupRef,
      liveAutoListenPendingRef,
      isCallActiveRef
    },
    liveModeActive,
    liveConnectRequested,
    liveSessionToken,
    liveVoiceCallEnabled,
    handleInputExternally,
    isWebrtcCall,
    isVisualOnly,
    isAvatarReady,
    runtimeUrl,
    projectTokenCamlin,
    builderDiagramId,
    liveSessionContext,
    linkedAvatarConfig,
    buildContextualChatLiveRuntimeContext,
    resolveLinkedAvatarModelId,
    clearLiveAutoListenTimer,
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    clearLiveVoiceRetryTimer,
    dispatchLiveBlendshapePlayback,
    dispatchAvatarEvent,
    finishLiveSpeaking,
    playLivePromptAudio,
    shouldSuppressInitialActiveListenPrompt,
    stopLiveSyntheticAudioSync,
    setLiveSessionContext,
    setLinkedAvatarModelId,
    setLinkedAvatarConfig,
    setLiveConnectionError,
    setLiveAssistantText,
    setDisplayText,
    setLiveChatText,
    setLiveChatEventId,
    setLivePromptFields,
    setLivePromptOptions,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal,
    setLiveTranscriptEventId,
    setIsCallActive,
    setIsWebrtcCall,
    setIsInVoiceCall,
    setLiveStatus
  });

  // Visual.linked has arrived once we've persisted either an avatar model id
  // or an avatar config payload from the runtime. Either signal is enough to
  // know the project's intended avatar — we don't need both. For the /live
  // route there is no visual handoff and `liveModeRequested` is true with
  // `isVisualOnly` false, so the runtime host's gate falls through and the
  // avatar mounts immediately.
  const hasReceivedVisualLink = Boolean(linkedAvatarModelId) || Boolean(linkedAvatarConfig);

  // Aggregate the avatar's health into one observable surface so:
  //   1. The on-screen <AvatarHealthBadge /> can render a visible pip
  //      (green/amber/red) on every consumer surface.
  //   2. External tooling (Sentry, Datadog) can subscribe to one
  //      `avatar:health` window event instead of duplicating the trap-
  //      point catalogue.
  // The hook is always-on (even on Visual IVR — the WebGL trap point is
  // relevant there too) and is purely additive observability.
  const avatarHealth = useAvatarHealth({
    turnCredentialsState,
    liveSessionStatus: liveSessionState?.status ?? null,
    liveConnectionError,
    enabled: true,
  });

  // Phase P3: forward the avatar's observability window events
  // (`avatar:health`, `avatar:live:status`) to the @camlin/analytics ingest
  // endpoint so avatar health + live-session lifecycle land in the same
  // cross-platform store as runtime flow + voice call events. Best-effort;
  // no-ops when no runtime URL is configured (e.g. pure visual-only embeds).
  useAvatarAnalytics({
    runtimeUrl,
    sessionId:
      liveSessionState?.context?.handoffToken ??
      liveSessionState?.context?.sessionId ??
      null,
    projectToken: projectTokenCamlin,
    diagramId: liveSessionState?.context?.diagramId ?? null,
    enabled: true,
  });

  // Mirror the live-session status as a dedicated window event so
  // consumers (AvatarCallSurface, AgentLink chat panel, anything
  // observing call lifecycle) can react to status transitions
  // without having to thread useHostedLiveController state through
  // every render. The avatar:health event is observability-shaped
  // (overall + issues array); this one is the lifecycle signal.
  // Status values: 'connecting' | 'ready' | 'listening' | 'thinking'
  // | 'speaking' | 'error' | 'idle' (and undefined when no session).
  const lastDispatchedLiveStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const status = liveSessionState?.status ?? null;
    if (lastDispatchedLiveStatusRef.current === status) return;
    lastDispatchedLiveStatusRef.current = status;
    window.dispatchEvent(
      new CustomEvent('avatar:live:status', {
        detail: { status, error: liveConnectionError ?? null },
      }),
    );
  }, [liveConnectionError, liveSessionState?.status]);

  return {
    activeAvatarModel,
    avatarHealth,
    resolvedModelUrl,
    liveAvatarAppearance,
    liveAvatarConfig,
    avatarLoadingMessage,
    browserAccessPrompt,
    canvasContainerSize,
    clearLiveAutoListenTimer,
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    clearLiveVoiceRetryTimer,
    closeBrowserAccessPrompt,
    currentSession,
    dispatchAvatarEvent,
    effectiveCameraZoom,
    motionParallaxEnabled,
    effectiveSkipTeethAnimation,
    ensureVoiceBrowserAccess,
    hasReceivedVisualLink,
    isAvatarReady,
    isInVoiceCall,
    isVoiceInitialized,
    initiateCall,
    liveAssistantText,
    liveChatEventId,
    liveChatText,
    liveConnectRequested,
    liveConnectionError,
    liveModeActive,
    livePromptFields,
    livePromptOptions,
    liveSessionContext,
    liveSessionState,
    liveTranscriptEventId,
    liveUserTranscript,
    liveUserTranscriptFinal,
    liveVoiceCallEnabled,
    markAvatarReady,
    refs,
    requestLiveSession,
    retryBrowserAccess,
    setIsInVoiceCall,
    setLiveAssistantText,
    setLiveConnectRequested,
    setLiveConnectionError,
    setLiveStatus,
    setLiveTranscriptEventId,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal,
    setPromptPlaybackUnlocked,
    sharedLiveAvatarConfig,
    stopLiveSyntheticAudioSync,
    handleLivePromptOptionSelect
  };
}

export default useHostedLiveController;
